<template>
  <div>
    <Modal :title="`部署 ${templateInfo.title}`" v-model="show" width="700">
      <a slot="extra" v-on:click="close" style="color: #000">
        <Icon type="md-close"/>
      </a>
      <Alert type="warning" v-if="errorMsg" show-icon>{{ errorMsg }}</Alert>
      <Alert type="success" v-if="successMsg" show-icon>{{ successMsg }}</Alert>
      <Form ref="deployForm" :model="deployForm" :rules="deployFormRules" :label-width="80">
        <FormItem label="节点" prop="node" style="width: 100%">
          <Select v-model="deployForm.node" placeholder="请选择要部署的节点" filterable>
            <Option v-for="item in nodes" :key="item.id" :value="item.id">{{ item.name }}</Option>
          </Select>
        </FormItem>
        <FormItem label="集群" prop="cluster" style="width: 100%">
          <Select v-model="deployForm.cluster" placeholder="部署到集群（可选）" :disabled="notModifyCluster" filterable>
            <Option v-for="item in clusters" :key="item.id" :value="item.id">{{ item.name }} {{ item.id }} 容器({{ Object.keys(item.configs).length }})</Option>
          </Select>
        </FormItem>
        <FormItem label="容器名称" prop="name" style="width: 100%">
          <Input v-model="deployForm.name" placeholder="容器名称（选填）"/>
        </FormItem>
        <FormItem label="网络模式" style="width: 100%" prop="network_mode">
          <Select v-model="deployForm.network_mode">
            <Option v-for="item in networkModeList" :key="item.value" :value="item.value">{{ item.label }}</Option>
          </Select>
        </FormItem>
        <FormItem label="Hostname" prop="hostname" style="width: 100%">
          <Input v-model="deployForm.hostname" placeholder="Hostname（选填）"/>
        </FormItem>
        <div v-if="templateInfo.config_variable">
          <Divider orientation="left">配置变量</Divider>
          <div v-for="(configVariableItem, configVariableItemIndex) in templateInfo.config_variable" :key="configVariableItemIndex">
            <Form ref="configVariableForm" :model="configVariableItem">
              <FormItem :label="configVariableItem.text" :prop="configVariableItem.text">
                <Input v-model="configVariables[configVariableItem.text]" :placeholder="`配置变量 \`${configVariableItem.text}\``"/>
              </FormItem>
            </Form>
          </div>
        </div>
        <div v-if="templateInfo.environments">
          <Divider orientation="left">环境变量</Divider>
          <div v-for="(envItem, envItemIndex) in templateInfo.environments" :key="envItemIndex" v-if="envItem.envMode === 'text' || envItem.envMode === 'select'">
            <Form ref="envForm" :model="envItem" :rules="environmentRules">
              <FormItem :label="envItem.envLabel" :prop="envItem.envName">
                <div v-if="envItem.envMode === 'text'">
                  <Input v-model="environments[envItem.envName]"/>
                </div>
                <div v-else-if="envItem.envMode === 'select'">
                  <Select v-model="environments[envItem.envName]">
                    <Option v-for="envOptionItem in envItem.envOption" :key="envOptionItem.envOptionValue" :value="envOptionItem.envOptionValue">{{ envOptionItem.envOptionName }}</Option>
                  </Select>
                </div>
                <span style="color: #999" v-if="envItem.envDescription">{{ envItem.envDescription }}</span>
              </FormItem>
            </Form>
          </div>
        </div>
        <div>
          <Divider orientation="left">端口映射<Button icon="md-add" type="success" size="small" style="margin: 6px 12px" @click="addPortsItem"></Button></Divider>
          <div v-for="(port, index) in portsData" :key="index">
            <Form ref="portsForm" :model="port" :rules="portsRules" inline>
              <Row>
                <Col :lg="6">
                  <FormItem prop="host_address" style="width: 100%" :label-width="5" label="">
                    <Input v-model="port.host_address" placeholder="主机监听地址" />
                  </FormItem>
                </Col>
                <Col :lg="6">
                  <FormItem prop="host_port" style="width: 100%" :label-width="5">
                    <Input v-model="port.host_port" placeholder="主机端口" />
                  </FormItem>
                </Col>
                <Col :lg="6">
                  <FormItem prop="protocol" style="width: 100%" :label-width="5" label="">
                    <Select v-model="port.protocol" placeholder="协议">
                      <Option v-for="item in protocolList" :key="item.value" :value="item.value">{{ item.label }}</Option>
                    </Select>
                  </FormItem>
                </Col>
                <Col :lg="6">
                  <FormItem prop="container_port" style="width: 100%" :label-width="5" label="">
                    <Input v-model="port.container_port" placeholder="容器端口">
                    <Button slot="append" icon="md-close" @click="removePortsItem(index)"></Button>
                    </Input>
                  </FormItem>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
        <div>
          <Divider orientation="left">目录映射<Button icon="md-add" type="success" size="small" style="margin: 6px 12px" @click="addVolumesItem"></Button></Divider>
          <div v-for="(volumes, index) in volumesData" :key="index">
            <Form ref="volumesForm" :model="volumes" :rules="volumesRules" inline>
              <Row>
                <Col :lg="9">
                  <FormItem prop="hostPath" style="width: 100%" :label-width="5">
                    <Input v-model="volumes.hostPath" placeholder="主机路径" />
                  </FormItem>
                </Col>
                <Col :lg="6">
                  <FormItem prop="mode" style="width: 100%" :label-width="5" label="">
                    <Select v-model="volumes.mode" placeholder="模式（默认读写）" clearable>
                      <Option v-for="item in volumesModeList" :key="item.value" :value="item.value">{{ item.label }}</Option>
                    </Select>
                  </FormItem>
                </Col>
                <Col :lg="9">
                  <FormItem prop="bind" style="width: 100%" :label-width="5" label="">
                    <Input v-model="volumes.bind" placeholder="容器路径">
                    <Button slot="append" icon="md-close" @click="removeVolumesItem(index)"></Button>
                    </Input>
                  </FormItem>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
        <Divider/>
      </Form>
      <div slot="footer">
        <Button type="success" style="margin-right: 10px" :loading="submitting" @click="submit">部署</Button>
        <Button @click="close">取消</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { getAllNodes } from '@/api/container/node'
import { getAllClusters } from '@/api/container/cluster'
import { detailContainerTemplate } from '@/api/container/template'
import { runContainer } from '@/api/container/container'

export default {
  name: 'deploy',
  data () {
    return {
      show: false,
      templateId: null,
      nodes: [],
      clusters: [],
      templateInfo: {},
      notModifyCluster: false,
      deployForm: {
        node: '',
        cluster: null,
        name: '',
        network_mode: '',
        hostname: ''
      },
      configVariables: {},
      // 环境变量
      environments: {},
      environmentRules: {},
      // 网络模式
      networkModeList: [
        { value: 'bridge', label: '桥接模式' },
        { value: 'host', label: '主机模式' },
        { value: 'none', label: '缺省模式' }
      ],
      deployFormRules: {
        node: { required: true, type: 'number', trigger: 'change', message: '请选择要部署的节点' },
        network_mode: { required: true, trigger: 'blur', message: '请选择网络模式' }
      },
      // 端口映射
      protocolList: [
        { value: 'tcp', label: 'TCP' },
        { value: 'udp', label: 'UDP' }
      ],
      portsData: [],
      portsRules: {
        container_port: { required: true, trigger: 'blur', message: '容器端口不能为空' },
        protocol: { required: true, trigger: 'change', message: '请选择协议' },
        host_address: { required: true, trigger: 'blur', message: '主机监听地址不能为空' },
        host_port: { required: true, trigger: 'blur', message: '主机断开不能为空' }
      },
      // 挂载目录
      volumesModeList: [
        { value: 'rw', label: '读和写' },
        { value: 'ro', label: '只读' },
        { value: 'shared', label: '共享' }
      ],
      volumesData: [],
      volumesRules: {
        hostPath: { required: true, trigger: 'blur', message: '主机路径不能为空' },
        bind: { required: true, trigger: 'blur', message: '容器路径不能为空' }
      },
      submitting: false,
      errorMsg: null,
      successMsg: null
    }
  },
  created: function () {
    this.getNodes()
    this.getClusters()
  },
  methods: {
    showModal (templateId, cluster = null) {
      this.templateId = templateId
      this.getTemplate()
      this.deployForm.cluster = cluster ? Number(cluster) : cluster
      this.notModifyCluster = !!cluster
      this.show = true
    },
    initForm () {
      this.deployForm = {
        node: '',
        cluster: null,
        name: '',
        network_mode: '',
        hostname: ''
      }
      this.environments = {}
      this.portsData = []
      this.volumesData = []
    },
    getNodes () {
      getAllNodes().then(res => {
        this.nodes = res.data
      }).catch(res => {
        console.log(res.response)
      })
    },
    getClusters () {
      getAllClusters().then(res => {
        this.clusters = res.data
      }).catch(res => {
        console.log(res.response)
      })
    },
    getTemplate () {
      this.initForm()
      detailContainerTemplate(this.templateId).then(res => {
        let templateInfo = res.data
        if (templateInfo.config) {
          this.deployForm.name = templateInfo.config.name
          this.deployForm.network_mode = templateInfo.config.network_mode
          this.deployForm.hostname = templateInfo.config.hostname
          if (templateInfo.config.volumes) {
            for (let i in templateInfo.config.volumes) {
              this.volumesData.push({
                hostPath: i,
                bind: templateInfo.config.volumes[i].bind,
                mode: templateInfo.config.volumes[i].mode
              })
            }
          }
          if (templateInfo.config.ports) {
            for (let i in templateInfo.config.ports) {
              const port = templateInfo.config.ports[i]
              let host = i.split('/')
              let protocol = 'tcp'
              if (host.length > 1) {
                protocol = host[1]
              }
              this.portsData.push({
                container_port: host[0],
                protocol: protocol,
                host_address: port[0],
                host_port: port[1]
              })
            }
          }
        }
        if (templateInfo.environments) {
          for (let i in templateInfo.environments) {
            this.environments[templateInfo.environments[i].envName] = templateInfo.environments[i].envValue
          }
        }
        if (templateInfo.config_variable) {
          for (let i in templateInfo.config_variable) {
            this.configVariables[templateInfo.config_variable[i].text] = ''
          }
        }
        this.templateInfo = templateInfo
      })
    },
    close () {
      this.show = false
      this.initForm()
    },
    addVolumesItem () {
      this.volumesData.push({
        hostPath: '',
        bind: '',
        mode: ''
      })
    },
    removeVolumesItem (index) {
      this.volumesData.splice(index, 1)
    },
    addPortsItem () {
      this.portsData.push({
        container_port: '',
        protocol: 'tcp',
        host_address: '0.0.0.0',
        host_port: ''
      })
    },
    removePortsItem (index) {
      this.portsData.splice(index, 1)
    },
    validForm () {
      let allValid = true
      this.$refs.deployForm.validate((valid) => {
        if (!valid) {
          allValid = false
        }
      })
      for (let i in this.$refs.envForm) {
        this.$refs.envForm[i].validate((valid) => {
          if (!valid) {
            allValid = false
          }
        })
      }
      for (let i in this.$refs.volumesForm) {
        this.$refs.volumesForm[i].validate((valid) => {
          if (!valid) {
            allValid = false
          }
        })
      }
      for (let i in this.$refs.portsForm) {
        this.$refs.portsForm[i].validate((valid) => {
          if (!valid) {
            allValid = false
          }
        })
      }
      return allValid
    },
    getRunReqData () {
      let reqData = this.templateInfo.config
      reqData.node = this.deployForm.node
      reqData.name = this.deployForm.name
      reqData.network_mode = this.deployForm.network_mode
      reqData.hostname = this.deployForm.hostname
      reqData.cluster = this.deployForm.cluster
      if (this.environments) {
        reqData.environment = []
        for (let i in this.environments) {
          reqData.environment.push(`${i}=${this.environments[i] ? this.environments[i] : ''}`)
        }
      }
      reqData.volumes = {}
      if (this.volumesData.length) {
        for (let i in this.volumesData) {
          const item = this.volumesData[i]
          reqData.volumes[item.hostPath] = {
            bind: item.bind,
            mode: item.mode
          }
        }
      }
      reqData.ports = {}
      if (this.portsData.length) {
        for (let i in this.portsData) {
          const item = this.portsData[i]
          reqData.ports[`${item.container_port}/${item.protocol}`] = [item.host_address, item.host_port]
        }
      }
      reqData.config_variable = this.configVariables
      return reqData
    },
    submit () {
      const allValid = this.validForm()
      if (allValid) {
        const reqData = this.getRunReqData()
        this.submitting = true
        runContainer(reqData).then(res => {
          this.$Notice.success({
            title: `开始部署「${res.data.Name} 」`,
            desc: `${res.data.Id}`
          })
          this.errorMsg = null
          this.successMsg = `开始部署「${res.data.Name} (${res.data.Id})」`
          this.show = false
          this.$emit('deploySuccess')
          this.submitting = false
        }).catch(res => {
          this.submitting = false
          console.log(res.response)
          if (res.request) {
            this.$Notice.error({
              title: '操作失败！',
              desc: res.request.responseText
            })
          } else {
            this.$Notice.error({
              title: '操作失败！'
            })
          }
          this.successMsg = null
          this.errorMsg = res.response.data
        })
      } else {
        this.$Notice.warning({
          title: '表单未填写完整，请检查！'
        })
      }
    }
  }
}
</script>

<style scoped>

</style>
